import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { APIURL } from "../globals";

// Session-based authentication - no need for localStorage/sessionStorage

export const api = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: APIURL,
    // "https://amldiligence.com/api",

    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  }),
  reducerPath: "adminApi",
  tagTypes: [
    "User",
    "Products",
    "Customers",
    "Transactions",
    "Geography",
    "Sales",
    "Admins",
    "Performance",
    "Dashboard",
    "Entities",
    "Individuals",
    "ExpiredIndividuals",
    "Logs",
    "IndividualsSearch",
    "RBA",
  ],

  endpoints: (build) => ({
      uploadFile: build.mutation({
        query: (file) => {
          const formData = new FormData();
          formData.append("uploadedFile", file);
          return {
            url: "/upload",
            method: "POST",
            body: formData,
          };
        },
      }),

      getCountries: build.query({
        query: (id) => `general/user/${id}`,
        providesTags: ["RBA"],
      }),

      editCountries: build.mutation({
        query: (country) => {
          console.log("Sending request with body:", country); // Logging the request body
          return {
            url: `general/rbaCountries`,
            method: "PUT",
            body: country,
          };
        },
        invalidatesTags: ["RBA"],
      }),

      editSourceOfFunds: build.mutation({
        query: (source) => {
          console.log("Sending request with body:", source); // Logging the request body
          return {
            url: `general/sourceOfFunds`,
            method: "PUT",
            body: source,
          };
        },
        invalidatesTags: ["RBA"],
      }),

      editIdType: build.mutation({
        query: (idType) => {
          console.log("Sending request with body:", idType); // Logging the request body
          return {
            url: `general/idType`,
            method: "PUT",
            body: idType,
          };
        },
        invalidatesTags: ["RBA"],
      }),

      editDeliveryChannel: build.mutation({
        query: (deliveryChannel) => {
          console.log("Sending request with body:", deliveryChannel); // Logging the request body
          return {
            url: `general/deliveryChannel`,
            method: "PUT",
            body: deliveryChannel,
          };
        },
        invalidatesTags: ["RBA"],
      }),

      editPep: build.mutation({
        query: (pep) => {
          console.log("Sending request with body:", pep); // Logging the request body
          return {
            url: `general/pep`,
            method: "PUT",
            body: pep,
          };
        },
        invalidatesTags: ["RBA"],
      }),

      editLicType: build.mutation({
        query: (licType) => {
          console.log("Sending request with body:", licType); // Logging the request body
          return {
            url: `general/licType`,
            method: "PUT",
            body: licType,
          };
        },
        invalidatesTags: ["RBA"],
      }),

      editResidentialStatus: build.mutation({
        query: (residentialStatus) => {
          console.log("Sending request with body:", residentialStatus); // Logging the request body
          return {
            url: `general/residentialStatus`,
            method: "PUT",
            body: residentialStatus,
          };
        },
        invalidatesTags: ["RBA"],
      }),

      editBusinessActivity: build.mutation({
        query: (businessActivity) => {
          console.log("Sending request with body:", businessActivity); // Logging the request body
          return {
            url: `general/businessActivity`,
            method: "PUT",
            body: businessActivity,
          };
        },
        invalidatesTags: ["RBA"],
      }),

      getUser: build.query({
        query: (id) => `general/user/${id}`,
        providesTags: ["User"],
      }),
      getProducts: build.query({
        query: () => "client/products",
        providesTags: ["Products"],
      }),

      getGoodsAndServices: build.query({
        query: () => "client/dpmsr", // Replace with the endpoint path
        providesTags: ["GoodsAndServices"], // Tag for cache management
      }),

      createGoodsAndServices: build.mutation({
        query: (newData) => ({
          url: "client/dpmsr", // Replace with the endpoint path
          method: "POST",
          body: newData, // Data to send in the request body
        }),
        invalidatesTags: ["GoodsAndServices"], // Invalidate cache to refresh the list
      }),

      deleteGoodsAndServices: build.mutation({
        query: (id) => ({
          url: `client/dpmsr/${id}`, // Dynamic ID in the URL
          method: "DELETE",
        }),
        invalidatesTags: (result, error, id) => [
          { type: "GoodsAndServices", id },
        ], // Invalidate specific item cache
      }),

      getLogs: build.query({
        query: ({
          start = 0,
          size = 10,
          filters = [],
          globalFilter = "",
          sorting = [],
        } = {}) => {
          const queryParams = new URLSearchParams({
            start: `${start}`,
            size: `${size}`,
            filters: JSON.stringify(filters),
            globalFilter,
            sorting: JSON.stringify(sorting),
          }).toString();
          return `/logs/screenlog?${queryParams}`;
        },
        providesTags: ["Logs"],
      }),

      getSearchEntities: build.query({
        query: ({ query = "", limit = 10 } = {}) => {
          const queryParams = new URLSearchParams({ query, limit }).toString();
          return `/client/searchEntities?${queryParams}`; // Adjust the endpoint as needed
        },
        providesTags: ["SearchEntities"],
      }),

      makePersonClear: build.mutation({
        query: ({ id, cleared, notes, ffr, nmr }) => ({
          url: "/client/clear",
          method: "POST",
          body: {
            id,
            cleared,
            notes,
            ffr,
            nmr,
          },
        }),
        invalidatesTags: ["Logs"],
      }),
      // ... other endpoints

      //Entities Queries
      getCustomers: build.query({
        query: (userId) => `client/entities/${userId}`,
        providesTags: ["Entities"],
      }),
      getExpiredCustomers: build.query({
        query: () => `client/expiredEntities`,
        providesTags: ["ExpiredCustomers"],
      }),
      postCustomers: build.mutation({
        query: (customer) => ({
          url: "client/entities",
          method: "POST",
          body: customer,
        }),
        invalidatesTags: ["Entities"],
      }),
      editCustomers: build.mutation({
        query: (customer) => ({
          url: `client/entities`,
          method: "PUT",
          body: customer,
        }),
        invalidatesTags: ["Entities", "Dashboard"],
      }),
      addComplianceQ: build.mutation({
        query: (id, responses) => ({
          url: `client/complianceQ`,
          method: "POST",
          body: { ...responses, ...id },
        }),

        invalidatesTags: ["Entities", "Dashboard"],
      }),
      addRiskProfile: build.mutation({
        query: (data) => ({
          url: `client/riskProfile`,
          method: "POST",
          body: { ...data },
        }),

        invalidatesTags: ["Entities", "Dashboard"],
      }),
      deleteCustomers: build.mutation({
        query: (id) => ({
          url: "client/entities",
          method: "DELETE",
          body: { id: id },
        }),
        invalidatesTags: ["Entities"],
      }),
      //End Entities Queries

      searchIndividuals: build.query({
        query: (searchCriteria) => ({
          url: "client/search",
          method: "POST",
          body: searchCriteria,
        }),
      }),
      getIndividuals: build.query({
        query: () => `client/individuals`,
        providesTags: ["Individuals"],
      }),
      getExpiredIndividuals: build.query({
        query: () => `client/expiredindividuals`,
        providesTags: ["ExpiredIndividuals"],
      }),
      postIndividuals: build.mutation({
        query: (customer) => ({
          url: "client/individuals",
          method: "POST",
          body: customer,
        }),
        invalidatesTags: ["Individuals", "Logs"],
      }),
      editIndividuals: build.mutation({
        query: (customer) => ({
          url: "client/individuals",
          method: "PUT",
          body: customer,
        }),
        invalidatesTags: ["Individuals"],
      }),
      deleteIndividuals: build.mutation({
        query: (id) => ({
          url: "client/individuals",
          method: "DELETE",
          body: { id: id },
        }),
        invalidatesTags: ["Individuals"],
      }),

      getCrpIndividual: build.mutation({
        query: (individualId) => ({
          url: "client/crpIndividual",
          method: "POST",
          body: { individual: individualId },
        }),
      }),

      //End Individuals Queries

      getCrp: build.query({
        query: (entity) => ({
          url: "client/crp",
          method: "POST",
          body: { entity: entity },
        }),
        invalidatesTags: ["Individuals"],
      }),
      getTransactions: build.query({
        query: ({ page, pageSize, sort, search }) => ({
          url: "client/transactions",
          method: "GET",
          params: { page, pageSize, sort, search },
        }),
        providesTags: ["Transactions"],
      }),
      getGeography: build.query({
        query: () => "client/geography",
        providesTags: ["Geography"],
      }),
      getSales: build.query({
        query: () => "sales/sales",
        providesTags: ["Sales"],
      }),
      getAdmins: build.query({
        query: () => "management/admins",
        providesTags: ["Admins"],
      }),
      getUserPerformance: build.query({
        query: (id) => `management/performance/${id}`,
        providesTags: ["Performance"],
      }),
      getDashboard: build.query({
        query: () => "general/dashboard",
        providesTags: ["Dashboard"],
      }),

      // Get current user info from session
      getMe: build.query({
        query: () => "general/me",
        providesTags: ["User"],
      }),
    }),
});

export const {
  useGetUserQuery,
  useGetProductsQuery,
  useGetTransactionsQuery,
  useGetGeographyQuery,
  useGetSalesQuery,
  useGetAdminsQuery,
  useGetUserPerformanceQuery,
  useGetDashboardQuery,
  useGetMeQuery,
  useGetCustomersQuery,
  usePostCustomersMutation,
  useDeleteCustomersMutation,
  useEditCustomersMutation,
  useGetIndividualsQuery,
  usePostIndividualsMutation,
  useSearchIndividualsQuery,
  useDeleteIndividualsMutation,
  useEditIndividualsMutation,
  useGetCrpIndividualMutation,
  useGetExpiredIndividualsQuery,
  useGetExpiredCustomersQuery,
  useGetLogsQuery,
  useAddComplianceQMutation,
  useAddRiskProfileMutation,
  useUploadFileMutation,
  useMakePersonClearMutation,
  useGetCountriesQuery,
  useEditCountriesMutation,
  useEditSourceOfFundsMutation,
  useEditBusinessActivityMutation,
  useEditIdTypeMutation,
  useEditLicTypeMutation,
  useEditResidentialStatusMutation,
  useEditPepMutation,
  useEditDeliveryChannelMutation,
  useGetCrpQuery,
  useGetSearchEntitiesQuery,
  useGetGoodsAndServicesQuery,
  useCreateGoodsAndServicesMutation,
  useDeleteGoodsAndServicesMutation,
} = api;
